import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { formatCurrency } from "../utils/currency";
import { useGlobalState } from "../GlobalStates";
import { getDDMMMDate } from "../utils/utils";
import { useUserLocale } from "../customHooks/useUserLocale";

export const TinyTripTag = ({
  style = {},
  status,
  num_slots_paid,
  total_slots,
  special_discount_name,
  special_discount_percentage,
  special_discount_end_date,
  price,
  newLaunch,
  trip_type,
  early_bird_percentage,
  free_reserve_allowed,
  user,
  start_date,
  is_dateless,
  is_leader,
  leader_requirement,
  earlyBirdRemainingSlots,
  leader_incentive_max,
  project_id,
  projectView = false,
  name,
  broad_category,
  view_participants_in_app,
}) => {
  const { t } = useTranslation();
  const { currency } = useUserLocale();
  const [projectCountData] = useGlobalState("projectCountData");

  if (
    !is_dateless &&
    projectCountData?.tripDates?.[project_id] > 1 &&
    projectCountData?.sellingOutDates?.[project_id] > 1 &&
    projectView
  ) {
    return (
      <div
        className="TinyTripStatusContainer"
        style={{
          backgroundColor: "rgba(255, 255, 255, 1)",
          border: "1px solid rgb(237, 237, 237)",
          color: "black",
          ...style,
        }}
      >
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("dates_selling_out", {
            count: projectCountData.sellingOutDates?.[project_id],
          })}
        </span>
      </div>
    );
  }

  if (status === "waitlist" && !projectView) {
    return (
      <div className="TinyTripStatusContainer" style={{ backgroundColor: "orange", ...style }}>
        <span className="TinyTripDetailsGroupSlotsSpan">{t("waitlist_access")}</span>
      </div>
    );
  }

  if (special_discount_percentage > 0) {
    let backgroundColor = "deeppink";

    if (special_discount_name === "auto_early_bird") {
      backgroundColor = "deepskyblue";
    }

    return (
      <div
        className="TinyTripStatusContainer"
        style={{ backgroundColor: backgroundColor, ...style }}
      >
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("special_discount", {
            end_date: getDDMMMDate(special_discount_end_date),
            savings: formatCurrency(
              Math.round((price * special_discount_percentage) / 100),
              null,
              true
            ),
          })}
        </span>
      </div>
    );
  }

  // if (is_leader && leader_requirement) {
  //   let backgroundColor = "purple";

  //   if (leader_requirement === "Junior duck") {
  //     backgroundColor = "deepskyblue";
  //   } else if (leader_requirement === "Senior duck") {
  //     backgroundColor = "#3f51b5"; // royal blue
  //   } else if (leader_requirement === "Super duck") {
  //     backgroundColor = "purple"; // purple
  //   }

  //   return (
  //     <div className="TinyTripStatusContainer" style={{ backgroundColor }}>
  //       <span className="TinyTripDetailsGroupSlotsSpan">
  //         {leader_requirement === "Junior duck"
  //           ? user.host_type === "Senior duck"
  //             ? t("senior_duck_and_above", {
  //               leader_incentive: formatCurrency(
  //                 Math.ceil((price * leader_incentive_max) / 100 / 10) * 10
  //               ),
  //             })
  //             : t("junior_duck_and_above", {
  //               leader_incentive: formatCurrency(Math.ceil((price * 25) / 100 / 10) * 10),
  //             })
  //           : t("super_duck_and_above", {
  //             leader_incentive: formatCurrency(
  //               Math.ceil((price * leader_incentive_max) / 100 / 10) * 10
  //             ),
  //           })}
  //       </span>
  //     </div>
  //   );
  // }

  if (num_slots_paid && num_slots_paid >= total_slots) {
    return (
      <div className="TinyTripStatusContainer" style={{ backgroundColor: "maroon", ...style }}>
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("sold_out_for_this_date", {
            defaultValue: "Sold out for this date",
          })}
        </span>
      </div>
    );
  }

  if (newLaunch === true) {
    return (
      <div className="TinyTripStatusContainer" style={{ backgroundColor: "deepskyblue", ...style }}>
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("new_to_sotravel", {
            defaultValue: "NEW TO SOTRAVEL",
          })}
        </span>
      </div>
    );
  }

  const isClubEvent =
    broad_category === "club" &&
    (trip_type === "event" || trip_type === "community");

  if (isClubEvent) {
    const spotsLeft = Number(total_slots || 0) - Number(num_slots_paid || 0);
    const isLastSpots = spotsLeft > 0 && spotsLeft <= 19;

    return (
      <div
        className="TinyTripStatusContainer"
        style={{ backgroundColor: isLastSpots ? "crimson" : "orange", ...style }}
      >
        <span className="TinyTripDetailsGroupSlotsSpan">
          {isLastSpots
            ? t("last_spots", { count: spotsLeft, defaultValue: "Last {{count}} spots" })
            : t("trending", { defaultValue: "Trending" })}
        </span>
      </div>
    );
  }

  // Only advertise remaining spots at the early-bird price when that tier is
  // active. Trips with early_bird_percentage=0 otherwise render a misleading
  // "0 spots at this price" badge.
  if (num_slots_paid && num_slots_paid / total_slots < 0.5 && earlyBirdRemainingSlots > 0) {
    return (
      <div className="TinyTripStatusContainer" style={{ backgroundColor: "orange", ...style }}>
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("early_bird_slots_left", {
            count: earlyBirdRemainingSlots,
            defaultValue: `${earlyBirdRemainingSlots} ${
              earlyBirdRemainingSlots === 1 ? "slot" : "slots"
            } left at this price`,
          })}
        </span>
      </div>
    );
  }

  if (num_slots_paid && total_slots - num_slots_paid === 1) {
    return (
      <div className="TinyTripStatusContainer" style={{ backgroundColor: "crimson", ...style }}>
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("last_spots", {
            count: total_slots - num_slots_paid,
          })}
        </span>
      </div>
    );
  }

  if (num_slots_paid && num_slots_paid / total_slots >= 0.75) {
    return (
      <div className="TinyTripStatusContainer" style={{ backgroundColor: "crimson", ...style }}>
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("last_spots", {
            count: total_slots - num_slots_paid,
          })}
        </span>
      </div>
    );
  }

  if (num_slots_paid) {
    // Organiser hid participant counts (trip.view_participants_in_app = false):
    // never expose booked/total numbers — show remaining spots instead.
    if (view_participants_in_app === false) {
      if (!total_slots || total_slots <= num_slots_paid) return null;
      return (
        <div className="TinyTripStatusContainer" style={{ backgroundColor: "crimson", ...style }}>
          <span className="TinyTripDetailsGroupSlotsSpan">
            {t("last_spots", {
              count: total_slots - num_slots_paid,
            })}
          </span>
        </div>
      );
    }

    return (
      <div
        className="TinyTripStatusContainer"
        style={{
          backgroundColor: "rgba(255, 255, 255, 1)",
          border: "1px solid rgb(237, 237, 237)",
          color: "black",
          ...style,
        }}
      >
        <span className="TinyTripDetailsGroupSlotsSpan">
          {t("slots_filled", {
            defaultValue: `${num_slots_paid} / ${total_slots} filled`,
            paid: num_slots_paid,
            total: total_slots,
          })}
        </span>
      </div>
    );
  }
};
